/**
 * ErrorBoundary — captura errores en tiempo de render de componentes hijos
 * y muestra una UI de fallback en lugar de una pantalla en blanco.
 *
 * React requiere que los Error Boundaries sean componentes de clase.
 *
 * @example
 * // Envolver una sección completa:
 * <ErrorBoundary>
 *   <NursesDetail ... />
 * </ErrorBoundary>
 *
 * // Con fallback personalizado:
 * <ErrorBoundary fallback={<p>No se pudo cargar este módulo.</p>}>
 *   <AccountEditor />
 * </ErrorBoundary>
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  children: ReactNode;
  /** UI alternativa a mostrar en caso de error (opcional) */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  /** Actualiza el estado cuando un hijo lanza un error durante el render */
  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  /** Loguea el error solo en desarrollo */
  componentDidCatch(error: Error, info: ErrorInfo) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorBoundary] Error capturado:', error);
      console.error('[ErrorBoundary] Stack del componente:', info.componentStack);
    }
  }

  /** Permite que el usuario intente recuperar el árbol de componentes */
  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 24px',
          textAlign: 'center',
          gap: 16,
        }}
      >
        <WarningAmberRoundedIcon
          sx={{ fontSize: 56, color: '#f59e0b' }}
        />
        <h2
          style={{
            margin: 0,
            fontSize: 20,
            fontWeight: 700,
            color: '#1e293b',
          }}
        >
          Algo salió mal
        </h2>
        <p
          style={{
            margin: 0,
            fontSize: 14,
            color: '#64748b',
            maxWidth: 420,
          }}
        >
          {this.state.error?.message ||
            'Ocurrió un error inesperado en esta sección. Puedes intentar recargarla.'}
        </p>
        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <Button variant="contained" onClick={this.handleReset}>
            Reintentar
          </Button>
          <Button variant="outlined" onClick={() => window.location.reload()}>
            Recargar página
          </Button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
